<script>
    import { onMount } from "svelte";
    import MatchSystemIcon from "@propublica/pp-v5-shared/src/icons/contrast--filled.svg";
    import PreferLightModeIcon from "@propublica/pp-v5-shared/src/icons/sun--filled.svg";
    import PreferDarkModeIcon from "@propublica/pp-v5-shared/src/icons/moon--filled.svg";
    import Button from "../Button";

    /**
     * Optional. Configures what the toggle button looks like. Provide an object
     * whose keys are any props of `Button`.
     *
     * If you specify *any* `Button` prop, then we recommend that you specify
     * *all* `Button` props that you want, or else they fall back to `Button`
     * defaults (not `DarkModeToggle` defaults), which might be surprising.
     *
     * For example, if you omit `buttonProps` entirely, then `iconOnly: true` is
     * used by default. But if you specify some object for `buttonProps` but
     * don't include the `iconOnly` key, then `iconOnly: false` (the `Button`
     * default) is used. In this case, you might want to add `iconOnly: true`
     * to your `buttonProps` object manually.
     *
     * @type {import("svelte").ComponentProps<Button>}
     */
    export let buttonProps = {
        bgColor: "var(--color-bg)",
        border: "1px solid var(--color-neutral-10)",
        textColor: "var(--color-text-supp)",
        buttonSize: "1.7rem",
        iconOnly: true,
        shape: "circle",
    };

    /**
     * @type {string}
     */
    let darkModeSetting = "auto";
    const darkModeValues = [
        { value: "auto", text: "Auto" },
        { value: "light", text: "Light" },
        { value: "dark", text: "Dark" },
    ];

    /**
     * @param {string} mode
     */
    function persistColorMode(mode) {
        if (mode === "auto") {
            localStorage.removeItem("dark-mode-setting");
        } else {
            localStorage.setItem("dark-mode-setting", mode);
        }

        const modeChangeEvent = new Event("darkModeSettingChange");
        window.dispatchEvent(modeChangeEvent);
    }

    /**
     * @param {string} mode
     * @param {boolean} persist - Whether or not to save dark mode preference to localStorage.
     */
    function setPreferredColorMode(mode, persist = false) {
        document.documentElement.classList.remove(
            "force-light-mode",
            "force-dark-mode",
        );

        if (mode !== "auto") {
            document.documentElement.classList.add(`force-${mode}-mode`);
        }

        if (persist) persistColorMode(mode);
    }

    // On initial page load, see if user has a color mode preference set already.
    onMount(() => {
        const savedDarkModeSetting = localStorage.getItem("dark-mode-setting");
        if (savedDarkModeSetting) {
            darkModeSetting = savedDarkModeSetting;
            setPreferredColorMode(darkModeSetting);
        }
    });
</script>

<div class="dark-mode-toggle">
    <Button
        elementType="button"
        {...buttonProps}
        miscAttrs={{ "aria-hidden": true }}
    >
        <svelte:fragment slot="iconBefore">
            {#if darkModeSetting === "light"}
                <PreferLightModeIcon />
            {:else if darkModeSetting === "dark"}
                <PreferDarkModeIcon />
            {:else}
                <MatchSystemIcon />
            {/if}
        </svelte:fragment>
        Change Appearance
    </Button>
    <label class="a11y" for="dark-mode-toggle__select">Change Appearance</label>
    <select
        bind:value={darkModeSetting}
        on:change={() => {
            setPreferredColorMode(darkModeSetting, true);
        }}
        class="dark-mode-toggle__select"
        id="dark-mode-toggle__select"
        data-pp-change
        data-pp-category="change-mode"
    >
        {#each darkModeValues as option}
            <option value={option.value}>{option.text}</option>
        {/each}
    </select>
</div>

<style>.dark-mode-toggle {
  height: var(--button-height);
  width: var(--button-width);
  position: relative;
  overflow: hidden;
}

.dark-mode-toggle__select {
  opacity: 0;
  padding: 0;
  display: inline-block;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
}
.dark-mode-toggle__select::-ms-expand {
  display: none;
}
.dark-mode-toggle__select:hover {
  cursor: pointer;
}</style>
